import { useMutation } from '@apollo/client';
import { useState } from 'react';
import {
    BudgetFieldsFragment,
    UpdateBudgetDocument,
} from '../../graphql/generated';
import { toDollars } from '../../util/currency';
import { useCurrentUser } from '../user';

type Timeframe = 'monthly' | 'annually';

export const TIMEFRAME_OPTIONS: { label: string; value: Timeframe }[] = [
    {
        label: 'Monthly',
        value: 'monthly',
    },
    {
        label: 'Annually',
        value: 'annually',
    },
];

export const useConfirmViewProps = ({
    budget,
    onComplete,
    onError,
}: {
    budget: BudgetFieldsFragment;
    onComplete: VoidFunction;
    onError: (err: unknown) => void;
}) => {
    const [timeframe, setTimeframe] = useState<Timeframe>('monthly');
    const { userInfo } = useCurrentUser();

    const [updateBudget, { loading: isSubmitting }] =
        useMutation(UpdateBudgetDocument);

    const handleSubmit = async () => {
        try {
            await updateBudget({
                variables: {
                    input: {
                        id: budget.id,
                        patch: {
                            isConfirmed: true,
                        },
                    },
                },
            });

            onComplete();
        } catch (e) {
            onError(e);
        }
    };

    const multiplier = timeframe === 'monthly' ? 1 : 12;

    return {
        timeframe,
        setTimeframe,
        handleSubmit,
        isSubmitting,
        incomeAmount: toDollars(
            userInfo.combinedOrIndividualMonthlyNetIncome * multiplier,
            { withCents: false, alwaysSigned: true }
        ),
        fixedAmount: toDollars(budget.fixedAmount * multiplier),
        lifestyleAmount: toDollars(budget.lifestyleAmount * multiplier),
        availableForSavingAmount: toDollars(
            (userInfo.combinedOrIndividualMonthlyNetIncome -
                budget.fixedAmount -
                budget.lifestyleAmount) *
                multiplier
        ),
    };
};
